import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';

import cx from 'classnames';
import chunk from 'lodash/chunk';
import { scrollToElement } from 'utils/scrollTo';

import CampaignContent from 'components/CampaignContent/CampaignContent';
import CampaignContentsSection from 'components/CampaignContentsSection/CampaignContentsSection';
import Card from 'components/Card/Card';
import FAQAccordionList from 'components/ContentRecord/components/FAQAccordionList/FAQAccordionList';
import DonateTodayWithSurfLifeSaverAvatar from 'components/DonateTodayWithSurfLifeSaverAvatar/DonateTodayWithSurfLifeSaverAvatar';
import DonationCampaignBannerSection from 'components/DonationCampaignBannerSection/DonationCampaignBannerSection';
import DonationOptionGridCommerce from 'components/DonationOptionGridCommerce/DonationOptionGridCommerce';
import DynamicHeadingLine from 'components/DynamicHeadingLine/DynamicHeadingLine';
import DynamicSlider from 'components/DynamicSlider/DynamicSlider';
import DonationWidgetBlock from 'components/PageBlocks/DonationWidgetBlock/DonationWidgetBlock';
import SectionWithContainer from 'components/PageBlocks/SectionWithContainer/SectionWithContainer';
import RowWithOffsetCol from 'components/RowWithOffsetCol/RowWithOffsetCol';
import SLSFProgressBar from 'components/SLSFProgressBar/SLSFProgressBar';
import { IDonationCampaign } from 'models/donationCampaign';
import { ISalesOrderItem } from 'models/salesOrderItem';
import { IThankYouMessage } from 'models/thankYouMessage';

import s from './ProductListDonationCampaignPage.module.scss';

const SLIDER_SETTINGS = {
  dots: false,
  infinite: true,
  speed: 500,
  autoplaySpeed: 5000,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  autoplay: true,
};

interface ProductListCampaignPageProps {
  donationCampaign: IDonationCampaign;
  messages: IThankYouMessage[];
  donations: ISalesOrderItem[];
  donateNowRef: React.RefObject<HTMLDivElement>;
}
const ProductListDonationCampaignPage: React.FC<
  ProductListCampaignPageProps
> = ({ donationCampaign, messages, donations, donateNowRef }) => {
  const topCampaignContent = useMemo(
    () => donationCampaign?.campaign_contents?.[0],
    [donationCampaign?.campaign_contents],
  );

  const restOfCampaignContents = useMemo(
    () => donationCampaign?.campaign_contents?.slice(1) || [],
    [donationCampaign?.campaign_contents],
  );

  return (
    <>
      <DonationCampaignBannerSection
        backgroundImageURL={donationCampaign.banner_image?.url}
        backgroundImageMobileURL={donationCampaign.mobile_banner_image?.url}
        bannerImageAlt={donationCampaign.banner_image_alt}
      >
        <Row>
          <div
            className={cx(
              'col-xl-6 col-lg-5 px-5 px-sm-2 px-md-3 pt-3 pb-2 pt-lg-9 pb-lg-8',
              s['banner-background-only-sm'],
            )}
          >
            {donationCampaign.heading && !donationCampaign.heading_image && (
              <h1
                className={cx(
                  'heading',
                  'heading-secondary',
                  'text-white',
                  s['heading-bold'],
                )}
              >
                {donationCampaign.heading}
              </h1>
            )}
            {donationCampaign.heading && donationCampaign.heading_image && (
              <img
                className="w-75 mb-4 mt-0 mt-md-5 mt-lg-0"
                src={donationCampaign.heading_image.url}
                alt={donationCampaign.heading_image_alt}
              />
            )}
            {donationCampaign.sub_heading && (
              <h2 className={cx('mb-4', 'text-white', s['sub-heading-bold'])}>
                {donationCampaign.sub_heading}
              </h2>
            )}
            {donationCampaign.goal_amount &&
              donationCampaign.goal_amount > 0 && (
                <SLSFProgressBar
                  amountAchieved={donationCampaign.raised_amount as number}
                  amountToAchieve={donationCampaign.goal_amount as number}
                />
              )}
          </div>
        </Row>
      </DonationCampaignBannerSection>
      {topCampaignContent && (
        <SectionWithContainer
          noPadding
          className="pt-0 pb-4 pt-4 mt-0 mt-lg-n10 position-relative z-100"
        >
          <CampaignContent
            campaignContent={topCampaignContent}
            colour={donationCampaign.colour}
            layout={topCampaignContent.layout?.layout_type}
          />
        </SectionWithContainer>
      )}
      {!!donationCampaign.donation_options?.length && (
        <SectionWithContainer>
          <Row className="mb-4">
            <Col className="offset-lg-1" lg={6}>
              <h1 data-content="campaign_content_heading" className="text-left">
                What you can give
              </h1>
              <DynamicHeadingLine
                className="mb-0"
                colour={donationCampaign.colour}
              />
            </Col>
          </Row>
          <DonationOptionGridCommerce
            donationCampaignOptions={donationCampaign.donation_options}
            donationCampaignSlug={donationCampaign.slug}
            underValues={[100, 500, 1000]}
            overValues={[1000]}
            imageClassName="object-fit-scale-down max-h-100px"
          />
        </SectionWithContainer>
      )}
      {donationCampaign.content_brief && (
        <SectionWithContainer
          style={{
            backgroundColor: donationCampaign.colour,
          }}
          className={cx(s['campaign-content-brief'], 'py-5')}
        >
          <Row>
            <Col lg={6} xl={7}>
              <div
                className={cx(
                  s['campaign-secondary-text'],
                  'text-white',
                  'font-weight-bold',
                )}
                data-content="donation_campaign_content_brief"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: donationCampaign.content_brief,
                }}
              />
            </Col>
          </Row>
        </SectionWithContainer>
      )}
      {!!messages.length && (
        <SectionWithContainer className="bg-light-grey">
          <RowWithOffsetCol>
            <h1 className="mb-5">
              <span>A wave of support for your lifesavers.</span>
              <DynamicHeadingLine
                colour={donationCampaign.colour}
                className="my-2"
              />
            </h1>
          </RowWithOffsetCol>
          <Row>
            <Col className="mb-3 d-flex" lg={6}>
              <Card className="no-shadow w-100">
                <Card.Body>
                  <DynamicSlider
                    className={s['main-slick-slider']}
                    {...SLIDER_SETTINGS}
                  >
                    {chunk<ISalesOrderItem>(donations, 3).map(donationChunk => (
                      <div key={donationChunk.map(({ id }) => id).join('-')}>
                        {donationChunk.map((donation: ISalesOrderItem) => (
                          <div key={donation.id} className="my-2">
                            <h3 className="text-primary mb-0">
                              {donation.sales_order?.customer?.first_name}{' '}
                              donated $
                              {parseInt(donation.total_item_amount || '0', 10)}
                            </h3>
                            <p>
                              to {donation.donation_campaign?.campaign_name}.
                              Thank-you!
                            </p>
                          </div>
                        ))}
                      </div>
                    ))}
                  </DynamicSlider>
                </Card.Body>
              </Card>
            </Col>
            <Col className="mb-3 d-flex" lg={6}>
              <Card className="no-shadow w-100">
                <Card.Body>
                  <DynamicSlider
                    className={s['main-slick-slider']}
                    {...SLIDER_SETTINGS}
                    fade
                    autoplaySpeed={4500}
                  >
                    {chunk<IThankYouMessage>(messages, 2).map(
                      thankYouMessageChunk => (
                        <div
                          key={thankYouMessageChunk
                            .map(({ id }) => id)
                            .join('-')}
                        >
                          {thankYouMessageChunk.map(
                            (thankYouMessage: IThankYouMessage) => (
                              <div key={thankYouMessage.id} className="my-2">
                                <h3 className="text-primary mb-2">
                                  {thankYouMessage?.customer?.first_name} says
                                </h3>
                                <p>"{thankYouMessage.message}"</p>
                              </div>
                            ),
                          )}
                        </div>
                      ),
                    )}
                  </DynamicSlider>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <RowWithOffsetCol className="mt-4" offset={3}>
            <DonateTodayWithSurfLifeSaverAvatar
              onDonateToday={() => {
                if (!donateNowRef.current) {
                  return;
                }
                scrollToElement(donateNowRef.current, 'top');
              }}
            />
          </RowWithOffsetCol>
        </SectionWithContainer>
      )}
      {!!restOfCampaignContents?.length && (
        <CampaignContentsSection
          campaignContents={restOfCampaignContents}
          colour={donationCampaign.colour}
        />
      )}
      {!!donationCampaign.featured_faqs?.length && (
        <SectionWithContainer>
          <Row className="mb-5">
            <Col lg={7}>
              <h2 className="heading heading-light-blue">
                Where does my money go? Can even a small donation help? Your
                questions answered.
              </h2>
            </Col>
          </Row>
          <RowWithOffsetCol>
            <FAQAccordionList faqs={donationCampaign.featured_faqs} />
          </RowWithOffsetCol>
        </SectionWithContainer>
      )}
      <DonationWidgetBlock
        donationCampaign={{ ...donationCampaign, donation_options: [] }}
        campaignBackgroundImageURL={donationCampaign.banner_image?.url}
        backdrop={false}
      />
    </>
  );
};

export default ProductListDonationCampaignPage;
